/**
 * Impact Notes — list page.
 * Parity with Angular `ImpactNotesListComponent`:
 *   - Paginated list, sortable by title / description / created_at.
 *   - Add / Edit via `ImpactNoteEditModal`.
 *   - Delete via confirm dialog.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Pencil, Trash2, ChevronsUpDown } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  ListingDataTableCard,
  LISTING_DATA_TABLE_CLASS,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { ImpactNoteEditModal } from '../components/ImpactNoteEditModal'
import {
  deleteImpactNote,
  getImpactNotes,
  saveImpactNote,
} from '../services/impactNotesService'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import configSortStyles from '@/components/listing/propertySortHeaderButton.module.css'
import { cn } from '@/lib/utils/helpers'
import type {
  ImpactNote,
  ImpactNoteListParams,
  ImpactNoteSortKey,
  ImpactNoteSubmitPayload,
  SortDirection,
} from '../types'

const DEFAULT_PARAMS: ImpactNoteListParams = {
  orderBy: 'created_at',
  sortBy: 'desc',
  page: 1,
}

function formatDate(unix: number | undefined): string {
  if (!unix) return '—'
  const date = new Date(unix * 1000)
  if (Number.isNaN(date.getTime())) return '—'
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = String(date.getFullYear()).slice(-2)
  return `${month}/${day}/${year}`
}

export function ImpactNotesListPage() {
  const [notes, setNotes] = useState<ImpactNote[]>([])
  const [params, setParams] = useState<ImpactNoteListParams>(DEFAULT_PARAMS)
  const [pagination, setPagination] = useState<{ current: number; last: number }>({
    current: 1,
    last: 1,
  })
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ImpactNote | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ImpactNote | null>(null)

  const mountedRef = useRef(false)

  const load = useCallback(async (next: ImpactNoteListParams) => {
    setLoading(true)
    try {
      const response = await getImpactNotes(next)
      setNotes(response.data ?? [])
      setPagination({ current: response.current ?? 1, last: response.last ?? 1 })
      setTotalRecords(response.total ?? 0)
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const applyParams = useCallback(
    (next: ImpactNoteListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: ImpactNoteSortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir, page: 1 })
  }

  const goToPage = (pageNo: number) => {
    applyParams({ ...params, page: pageNo })
  }

  const openAdd = () => {
    setEditTarget(null)
    setEditOpen(true)
  }

  const openEdit = (note: ImpactNote) => {
    setEditTarget(note)
    setEditOpen(true)
  }

  const handleSubmit = async (payload: ImpactNoteSubmitPayload, id?: number) => {
    setSubmitting(true)
    try {
      await saveImpactNote(payload, id)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      // New rows should appear at the top: match default list sort (newest first) and page 1.
      if (id) {
        void load(params)
      } else {
        const next = { ...DEFAULT_PARAMS, page: 1 }
        setParams(next)
        void load(next)
      }
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteImpactNote(deleteTarget.id)
      showSuccess('App Impact Notes Deleted Successfully!')
      setDeleteTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    }
  }

  const SortHeader = ({ column, label, className }: { column: ImpactNoteSortKey; label: string; className?: string }) => {
    const isActive = params.orderBy === column
    return (
      <th
        className={cn("select-none cursor-pointer !text-left", className)}
        onClick={() => toggleSort(column)}
      >
        <button
          type="button"
          className={cn(parityStyles.sortHeaderButton, configSortStyles.propertySortHeaderButton, 'justify-start')}
          onClick={(event) => {
            event.stopPropagation()
            toggleSort(column)
          }}
        >
          <span className={parityStyles.sortHeaderLabel}>{label}</span>
          <span
            aria-hidden="true"
            className={cn(parityStyles.sortIcon, isActive && parityStyles.sortIconActive)}
          >
            {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
          </span>
        </button>
      </th>
    )
  }

  return (
    <PageShell
      title="IMPACT NOTES"
      headerAction={
        <button
          type="button"
          onClick={openAdd}
          className={cn(primaryEntityActionButtonClass, "w-full sm:w-auto")}
        >
          Add
        </button>
      }
    >

      <ListingDataTableCard>
        <div className="w-full">
          <table className={cn(parityStyles.propertyListingTable, parityStyles.listingTable, LISTING_DATA_TABLE_CLASS)}>
            <thead className="border-b border-[#c8d7e8] bg-[#dfeaf5] text-slate-900">
              <tr>
                <SortHeader column="title" label="Title" className="w-1/4" />
                <SortHeader column="description" label="Description" className="w-1/4" />
                <SortHeader column="created_at" label="Date" className="w-1/4" />
                <th className="px-2 py-2 !text-center text-xs sm:px-4 sm:py-3 sm:text-sm w-1/4">Action</th>
              </tr>
            </thead>
          <tbody className="divide-y divide-[#dfe7f0] [&>tr:nth-child(even)]:bg-[#f2f7fd]">
            {loading ? (
              <ListingTableLoadingRow colSpan={4} />
            ) : notes.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-sm text-slate-500 sm:px-4 sm:py-8">
                  No impact notes yet.
                </td>
              </tr>
            ) : (
              notes.map((note) => (
                <tr key={note.id} className={parityStyles.listingTableRow}>
                  <td className="px-2 py-2 text-sm font-medium text-slate-900 sm:px-4 sm:py-3 sm:text-base">
                    {note.title}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-700 sm:px-4 sm:py-3 sm:text-base">
                    {(note.description || '').slice(0, 40)}
                    {(note.description || '').length > 40 ? '…' : ''}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs text-slate-600 sm:px-4 sm:py-3 sm:text-sm">
                    {formatDate(note.from_date)} – {formatDate(note.to_date)}
                  </td>
                  <td className="px-2 py-2 sm:px-4 sm:py-3">
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(note)}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(note)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        aria-label="Delete"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </ListingDataTableCard>

      {pagination.last > 1 && (
        <ListPaginationBar
          placement="footer"
          testIdPrefix="impact-notes"
          currentPage={params.page || 1}
          lastPage={pagination.last}
          totalRecords={totalRecords}
          perPage={15}
          onPageChange={goToPage}
          onPerPageChange={() => {}}
          showPerPageSelect={false}
        />
      )}

      <ImpactNoteEditModal
        isOpen={editOpen}
        note={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Impact Note"
        message="Do You Want To Delete this App Impact Notes Detail ?"
        primaryLabel="Delete"
        onPrimary={confirmDelete}
        secondaryLabel="Cancel"
        onSecondary={() => setDeleteTarget(null)}
        variant="warning"
      />
    </PageShell>
  )
}
